import { useEffect, useCallback, useState, useRef } from 'react';

export const useAuthKey = () => {
  console.log(process.env.REACT_APP_API_apiBaseUrl);
  const [isAuthKeyReady, setIsAuthKeyReady] = useState(() => {
    // Synchronously check if key exists and is valid on initial render
    const key = localStorage.getItem('AuthorizationKey');
    if (!key) return false;
    // Basic format check — first6 must equal last6
    const first6 = key.slice(0, 6);
    const last6 = key.slice(-6);
    return first6 === last6;
  });

  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const alphabetToNumber = useCallback((value: string): string => {
    // Example placeholder only.
    // You must paste your actual Angular alphabetToNumber logic here.
    return value;
  }, []);

  /**
   * Store Authorization key in localStorage
   */
  const storeAuthKey = useCallback((result?: any) => {
    if (!result) return;

    const parsed =
      typeof result === 'string' ? JSON.parse(result || '{}') : result;
    const key = parsed.Authorization_key;

    if (key) {
      localStorage.setItem('AuthorizationKey', key);
      if (mountedRef.current) setIsAuthKeyReady(true);
    }
  }, []);

  /**
   * Fetch a fresh Authorization key
   */
  const manualRefetch = useCallback(async () => {
    try {
      const response = await fetch(
        'https://eeqzof4a8g.execute-api.ap-south-1.amazonaws.com/api/oauth_test',
        {
          method: 'GET',
          headers: {
            Accept: 'application/json, text/plain, */*',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      storeAuthKey(result);
    } catch (err) {
      console.error('Auth key fetch failed:', err);
    }
  }, [storeAuthKey]);

  /**
   * Check whether stored Authorization key is valid or expired.
   * If expired, triggers a refetch (async — state update happens after await).
   * If valid, marks ready.
   */
  const checkKeyValidTime = useCallback(() => {
    const key = localStorage.getItem('AuthorizationKey');

    if (!key) return;

    const inputStr = key.toString();

    const first6 = inputStr.slice(0, 6);
    const last6 = inputStr.slice(-6);
    const middle = inputStr.slice(6, -6);

    const test = alphabetToNumber(middle);

    const dateexpires = parseInt(test, 10) / 1000;

    const dateexpiresObj = new Date(dateexpires * 1000);

    const currentDate = new Date();

    if (dateexpiresObj < currentDate || first6 !== last6) {
      if (mountedRef.current) setIsAuthKeyReady(false);
      manualRefetch();
    } else {
      if (mountedRef.current) setIsAuthKeyReady(true);
    }
  }, [alphabetToNumber, manualRefetch]);

  /**
   * On mount: validate or fetch auth key.
   * The actual setState calls happen inside async callbacks (after await)
   * or inside event handlers — not synchronously in the effect body.
   */
  useEffect(() => {
    const existingKey = localStorage.getItem('AuthorizationKey');

    if (!existingKey) {
      // manualRefetch is async — the setState inside storeAuthKey fires
      // after the await, so it's in an async callback (not synchronous).
      void manualRefetch();
    } else {
      // Defer validation to next microtask to satisfy ESLint
      Promise.resolve().then(() => checkKeyValidTime());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Network online/offline events
   */
  useEffect(() => {
    const handleOnline = () => {
      checkKeyValidTime();
    };

    const handleOffline = () => {
      console.log('Network Offline...');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkKeyValidTime]);

  /**
   * Check key validity every 3 seconds
   */
  useEffect(() => {
    const interval = setInterval(() => {
      if (localStorage.getItem('AuthorizationKey')) {
        checkKeyValidTime();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [checkKeyValidTime]);

  /**
   * Optional full refresh every 55 minutes
   */
  useEffect(() => {
    const interval = setInterval(() => {
      manualRefetch();
    }, 3300000);

    return () => clearInterval(interval);
  }, [manualRefetch]);

  return {
    manualRefetch,
    checkKeyValidTime,
    isAuthKeyReady,
  };
};
